import { useEffect } from "react";
import { motion } from "framer-motion";
import { useLocation } from "react-router-dom";
import FutureOpenings from "../components/home/FutureOpenings";
import SEO from "../components/SEO";
import NumberCounter from "../components/NumberCounter";
import { usePageContent } from "../lib/usePageContent";

export default function NuoveAperture() {
  const { hash } = useLocation();
  const c = usePageContent("nuove-aperture", {});

  // Smooth-scroll to #opening-<slug> once the cards render
  useEffect(() => {
    if (!hash) return;
    const id = hash.slice(1);
    let attempts = 0;
    const tick = () => {
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
        return;
      }
      if (attempts++ < 20) setTimeout(tick, 150);
    };
    tick();
  }, [hash]);

  return (
    <div data-testid="nuove-aperture-page">
      <SEO
        title="Nuove Aperture"
        path="/nuove-aperture"
        description="5 nuove strutture entro il 2027 per ulteriori 674 camere. Scopri la pipeline: Bloom, Breda Suites, Palazzo Cornalia, Gateway, Kastà e The Cathedral Duomo."
      />
      {/* Custom typographic hero (no misleading stock photos) */}
      <section className="relative min-h-[70svh] flex items-end overflow-hidden bg-[#08080A]">
        <div className="absolute inset-0 opacity-70">
          <div className="absolute -top-40 left-1/4 h-[600px] w-[600px] rounded-full bg-[#C48F56] blur-[200px] opacity-20" />
          <div className="absolute bottom-0 right-1/4 h-[500px] w-[500px] rounded-full bg-[#70142A] blur-[180px] opacity-15" />
        </div>
        <div
          className="absolute inset-0 opacity-[0.06] mix-blend-overlay pointer-events-none"
          style={{
            backgroundImage:
              "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='160' height='160'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2'/></filter><rect width='100%25' height='100%25' filter='url(%23n)'/></svg>\")",
          }}
        />
        {/* large faint "2026/2027" typographic watermark */}
        <div className="absolute top-1/4 right-[-4%] font-display italic font-light text-white/[0.03] text-[28vw] leading-none pointer-events-none select-none">
          2027
        </div>

        <div className="relative pt-40 pb-20 md:pb-28 px-6 md:px-12 max-w-[1440px] mx-auto w-full">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          >
            <div className="flex items-center gap-3 text-[11px] uppercase tracking-[0.3em] text-white/60">
              <span className="h-px w-10 bg-[#C48F56]" />
              {c.eyebrow || "Progetti futuri · 2026 — 2027"}
            </div>
            <h1 className="mt-6 font-display text-balance tracking-tight text-white text-3xl sm:text-4xl md:text-5xl lg:text-5xl leading-[1.05] font-medium">
              {c.title_line_1 || "Cinque cantieri,"}<br />
              <span className="text-[#C48F56]">{c.title_line_2 || "un'unica visione."}</span>
            </h1>
            <div className="mt-10 grid md:grid-cols-3 gap-8 max-w-4xl">
              <Stat label={c.stat_openings_label || "Nuove aperture"} value={c.stat_openings_value || 6} />
              <Stat label={c.stat_rooms_label || "Camere aggiuntive"} value={c.stat_rooms_value || 674} accent />
              <Stat
                label={c.stat_horizon_label || "Orizzonte"}
                value={c.stat_horizon_value || "2026 – 2027"}
                staticValue
              />
            </div>
            <p className="mt-10 max-w-2xl text-white/70 text-lg leading-relaxed">
              {c.subhead || "Nuovi progetti stanno prendendo forma in aree strategiche di Milano. Dal restauro della storica Casa Galli Tassara in Piazza Cinque Giornate al primo cinque stelle del gruppo in Piazza Duomo."}
            </p>
          </motion.div>
        </div>
      </section>

      <FutureOpenings />
    </div>
  );
}

function Stat({ label, value, accent, staticValue }) {
  return (
    <div className="border-l border-white/10 pl-5">
      <div
        className={`font-display text-5xl md:text-6xl tracking-tight leading-none ${
          accent ? "text-[#C48F56]" : ""
        }`}
      >
        {staticValue ? value : <NumberCounter value={value} />}
      </div>
      <div className="mt-2 font-mono text-[10px] uppercase tracking-[0.25em] text-white/40">
        {label}
      </div>
    </div>
  );
}
